/**
 * The published Content Studio bundle, frozen at build time.
 *
 * PAWTENANT-CONTENT-STUDIO-INTEGRATION-001
 *
 * scripts/cms/fetch.mjs (the FIRST build step) writes src/generated/cmsBundle.json.
 * This module imports that file, so the client bundle, the SSR prerender
 * (src/prerender/entry.tsx) and the runtime SEO manager all read the SAME
 * frozen copy: no network, no loading state, server and client identical.
 * The committed default is an empty bundle, which renders the site exactly as
 * it was before the studio existed.
 */

import generated from "@/generated/cmsBundle.json";
import type { CmsBundle, CmsCreatedPage, CmsOverrideValue, CmsPageMeta, CmsPagePublication, CmsRedirect } from "./types";

function normalizePath(pathname: string): string {
  let p = (pathname || "/").split("?")[0].split("#")[0];
  if (!p.startsWith("/")) p = `/${p}`;
  if (p.length > 1) p = p.replace(/\/+$/, "");
  return p || "/";
}

function sanitize(raw: unknown): CmsBundle {
  const b = (raw && typeof raw === "object" ? raw : {}) as Partial<CmsBundle>;
  const removed = Array.isArray(b.removed) ? b.removed.filter((p): p is string => typeof p === "string").map(normalizePath) : [];
  const removedSet = new Set(removed);
  const created = (Array.isArray(b.created) ? b.created : [])
    .filter((c): c is CmsCreatedPage => !!c && typeof c === "object" && typeof c.path === "string" && c.path.startsWith("/"))
    .map((c) => ({ ...c, path: normalizePath(c.path), blocks: Array.isArray(c.blocks) ? c.blocks : [] }))
    .filter((c) => !removedSet.has(c.path));
  return {
    website: b.website && typeof b.website === "object" ? b.website : { slug: "", public_origin: "" },
    bundle_id: typeof b.bundle_id === "string" ? b.bundle_id : "none",
    generated_at: typeof b.generated_at === "string" ? b.generated_at : "",
    assets_base: typeof b.assets_base === "string" ? b.assets_base : "",
    pages: b.pages && typeof b.pages === "object" && !Array.isArray(b.pages) ? b.pages : {},
    created,
    removed,
    redirects: (Array.isArray(b.redirects) ? b.redirects : []).filter(
      (r): r is CmsRedirect => !!r && typeof r.from === "string" && typeof r.to === "string" && r.from.startsWith("/") && r.to.startsWith("/"),
    ),
  };
}

let current: CmsBundle = sanitize(generated);

/** The frozen bundle. */
export function cmsBundle(): CmsBundle {
  return current;
}

/**
 * Test seam for scripts/cms/check.mjs ONLY: swap the bundle so the offline
 * sample bundle can be rendered through the real components without a
 * rebuild. Never called by application code; the client bundle only ever
 * holds the generated file.
 */
export function __setCmsBundleForTests(raw: unknown | null): void {
  current = sanitize(raw ?? generated);
}

/** The live publication for a compiled page (metadata and/or section overrides). */
export function cmsPagePublication(pathname: string): CmsPagePublication | null {
  const p = current.pages[normalizePath(pathname)];
  return p && typeof p === "object" && typeof p.publication_id === "string" ? p : null;
}

function cleanMeta(meta: CmsPageMeta | undefined | null): CmsPageMeta {
  const out: CmsPageMeta = {};
  if (!meta || typeof meta !== "object") return out;
  for (const k of ["title", "description", "og_title", "og_description", "og_image"] as const) {
    const v = meta[k];
    if (typeof v === "string" && v.trim()) out[k] = v.trim();
  }
  return out;
}

/**
 * Head metadata the studio controls for a path: the override on a compiled
 * page, or — for a created page — its own meta with the article's title /
 * excerpt / hero as defaults. Null when the studio has nothing for the path.
 */
export function cmsPageMeta(pathname: string): CmsPageMeta | null {
  const pub = cmsPagePublication(pathname);
  if (pub) {
    const out = cleanMeta(pub.meta);
    return Object.keys(out).length ? out : null;
  }
  const created = cmsCreatedPage(pathname);
  if (!created) return null;
  const out = cleanMeta(created.meta);
  if (!out.title) out.title = `${created.title} | PawTenant`;
  if (!out.description) out.description = created.excerpt || created.title;
  if (!out.og_image && created.hero?.src) out.og_image = created.hero.src;
  return out;
}

/** Section-map overrides for a compiled page, or null when nothing is published. */
export function cmsOverrides(pathname: string): Record<string, CmsOverrideValue> | null {
  const o = cmsPagePublication(pathname)?.overrides;
  return o && typeof o === "object" && !Array.isArray(o) ? o : null;
}

/** Studio-created pages (removed ones already excluded). */
export function cmsCreatedPages(): CmsCreatedPage[] {
  return current.created;
}

export function cmsCreatedPage(pathname: string): CmsCreatedPage | null {
  const p = normalizePath(pathname);
  return current.created.find((c) => c.path === p) ?? null;
}

export function cmsRedirects(): CmsRedirect[] {
  return current.redirects;
}

/** publication id for any path that carries studio content (compiled override or created page). */
export function cmsPublicationId(pathname: string): string | null {
  return cmsPagePublication(pathname)?.publication_id ?? cmsCreatedPage(pathname)?.publication_id ?? null;
}

/** Created blog posts, newest first by published_at (ties: updated_at, then path). */
export function cmsCreatedBlogPosts(): CmsCreatedPage[] {
  return current.created
    .filter((c) => c.kind === "blog" || c.path.startsWith("/blog/"))
    .slice()
    .sort((a, b) => (b.published_at || "").localeCompare(a.published_at || "") || (b.updated_at || "").localeCompare(a.updated_at || "") || a.path.localeCompare(b.path));
}
